import { attr, belongsTo } from "@ember-data/model";
import {
  validateInclusion,
  validateNumber,
  validatePresence
} from "ember-changeset-validations/validators";
import type IntlService from "ember-intl/services/intl";
import JournalEvent from "parog-web/models/journal-event";
import type Patient from "parog-web/models/patient";

export interface TreatmentSectionWithColor {
  key: string;
  label: string;
  color?: string;
  answers: string[];
}

type FatigueStringEnum = "0" | "1" | "2";

type CoughStringEnum = "0" | "1" | "2" | "3" | "4";

type BreathStringEnum = "0" | "1" | "2" | "3" | "4";

type NauseaVomitingStringEnum = "0" | "1" | "2";

type DiarrheaStringEnum = "0" | "1" | "2";

type ConstipationStringEnum = "0" | "1";

type MuscularPainLevelStringEnum = "0" | "1" | "2";

type ArticularPainLevelStringEnum = "0" | "1" | "2";

export default abstract class BaseTreatmentFilledJournalEvent extends JournalEvent {
  static type = "pre-treatment-filled-journal-event";

  @attr("boolean", {
    allowNull: true
  })
  declare temperatureIssue: boolean;

  @attr("number") declare temperature: number;

  @attr("boolean", {
    allowNull: true
  })
  declare fatigueIssue: boolean;

  @attr("string") declare fatigue: FatigueStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare coughIssue: boolean;

  @attr("string") declare cough: CoughStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare breathIssue: boolean;

  @attr("string") declare breath: BreathStringEnum;

  @attr("number") declare currentWeight: number;

  @attr("boolean", {
    allowNull: true
  })
  declare weightLossIssue: boolean;

  @attr("number") declare weightLoss: number;

  @attr("boolean", {
    allowNull: true
  })
  declare dermatologicalIssue: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare skinRashIssue: boolean;

  @attr("string") declare skinRashLocation: string;

  @attr("boolean", {
    allowNull: true
  })
  declare itchingIssue: boolean;

  @attr("string") declare itchingLocation: string;

  @attr("boolean", {
    allowNull: true
  })
  declare dermatologicalTreatmentEfficient: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare nauseaVomitingIssue: boolean;

  @attr("string") declare nauseaVomiting: NauseaVomitingStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare diarrheaIssue: boolean;

  @attr("string") declare diarrhea: DiarrheaStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare constipationIssue: boolean;

  @attr("string") declare constipation: ConstipationStringEnum;

  @attr("boolean", {
    allowNull: true
  })
  declare constipationTreatmentEfficient: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare painIssue: boolean;

  @attr("number") declare pain: number;

  @attr("boolean", {
    allowNull: true
  })
  declare muscularPainIssue: boolean;

  @attr("string") declare muscularPainLevel: MuscularPainLevelStringEnum;

  @attr("string") declare muscularPainLocation: string;

  @attr("boolean", {
    allowNull: true
  })
  declare muscularPainTreatmentEfficient: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare articularPainIssue: boolean;

  @attr("string") declare articularPainLevel: ArticularPainLevelStringEnum;

  @attr("string") declare articularPainLocation: string;

  @attr("boolean", {
    allowNull: true
  })
  declare articularPainTreatmentEfficient: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare otherPainIssue: boolean;

  @attr("string") declare otherPainLocations: string;

  @attr("boolean", {
    allowNull: true
  })
  declare otherPainTreatmentEfficient: boolean;

  @attr("boolean", {
    allowNull: true
  })
  declare hasAlertingValue: boolean;

  @attr("string") declare originTaskIdentifier: string;

  static validations(intl: IntlService) {
    return {
      currentWeight: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.current-weight"
        )
      }),
      weightLoss: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.weight-loss"
        )
      }),
      pain: validateNumber({
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.pain"
        )
      }),
      temperature: validateInclusion({
        list: ["1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.temperature"
        )
      }),
      fatigue: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.fatigue"
        )
      }),
      cough: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.cough"
        )
      }),
      breath: validateInclusion({
        list: ["0", "1", "2", "3", "4"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.breath"
        )
      }),
      nauseaVomiting: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.nausea-vomiting"
        )
      }),
      diarrhea: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.diarrhea"
        )
      }),
      constipation: validateInclusion({
        list: ["0", "1"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.constipation"
        )
      }),
      muscularPainLevel: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.muscular-pain-level"
        )
      }),
      articularPainLevel: validateInclusion({
        list: ["0", "1", "2"],
        allowBlank: true,
        description: intl.t(
          "validations.pre-treatment-filled-journal-event.articular-pain-level"
        )
      }),
      occurredAt: validatePresence({
        presence: true,
        description: intl.t("validations.occurred-at")
      })
    };
  }

  //---- End of generated code ----

  @belongsTo("patient", {
    async: false,
    inverse: "preTreatmentFilledJournalEvents"
  })
  declare patient: Patient;

  static baseSections = [
    "temperatureIssue",
    "temperature",
    "fatigueIssue",
    "fatigue",
    "coughIssue",
    "cough",
    "breathIssue",
    "breath",
    "currentWeight",
    "weightLossIssue",
    "weightLoss",
    "pacPainful",
    "pacInflammatory",
    "pacFlowing",
    "pacHealing",
    "dermatologicalIssue",
    "skinRashIssue",
    "skinRashLocation",
    "itchingIssue",
    "itchingLocation",
    "dermatologicalTreatmentEfficient",
    "skinRashIssue",
    "skinRashLocation",
    "itchingIssue",
    "itchingLocation",
    "nauseaVomitingIssue",
    "nauseaVomiting",
    "diarrheaIssue",
    "diarrhea",
    "constipationIssue",
    "constipation",
    "constipationTreatmentEfficient",
    "painIssue",
    "pain",
    "muscularPainIssue",
    "muscularPainLevel",
    "muscularPainLocation",
    "muscularPainTreatmentEfficient",
    "articularPainIssue",
    "articularPainLevel",
    "articularPainLocation",
    "articularPainTreatmentEfficient",
    "otherPainIssue",
    "otherPainLocation",
    "otherPainTreatmentEfficient",
    "cortisone",
    "cortisoneDosageGt20",
    "radiotherapy"
  ];

  abstract get sectionsWithColor(): TreatmentSectionWithColor[];

  get baseSectionsWithColor(): TreatmentSectionWithColor[] {
    return [
      {
        key: "temperature",
        label: `Température`,
        color: `${
          this.temperatureIssue
            ? ((temperatureValue: number) => {
                if (temperatureValue > 38.5) {
                  return "red";
                }
                if (temperatureValue >= 38) {
                  return "orange";
                }
                return "green";
              })(this.temperature)
            : "green"
        }`,
        answers: this.temperatureIssue
          ? [`Température : ${this.temperature}°C`]
          : ["Non"]
      },
      {
        key: "fatigue",
        label: "Fatigue",
        color: `${
          this.fatigueIssue
            ? ((fatigueValue: FatigueStringEnum) => {
                if (fatigueValue == "2") {
                  return "red";
                }
                if (fatigueValue == "1") {
                  return "orange";
                }
                return "green";
              })(this.fatigue)
            : "green"
        }`,
        answers: this.fatigueIssue
          ? ((fatigueValue: FatigueStringEnum) => {
              switch (fatigueValue) {
                case "2":
                  return ["Sévère"];
                case "1":
                  return ["Modérée"];
                case "0":
                default:
                  return ["Légère"];
              }
            })(this.fatigue)
          : ["Non"]
      },
      {
        key: "cough",
        label: "Toux",
        color: `${
          this.coughIssue
            ? ((coughValue: CoughStringEnum) => {
                if (coughValue == "4") {
                  return "red";
                }
                if (coughValue == "3") {
                  return "orange";
                }
                return "green";
              })(this.cough)
            : "green"
        }`,
        answers: this.coughIssue
          ? ((coughValue: CoughStringEnum) => {
              switch (coughValue) {
                case "4":
                  return ["Très sévère"];
                case "3":
                  return ["Sévère"];
                case "2":
                  return ["Modérée"];
                case "1":
                  return ["Légère"];
                case "0":
                default:
                  return ["Aucune"];
              }
            })(this.cough)
          : ["Non"]
      },
      {
        key: "breath",
        label: "Difficultés à respirer",
        color: `${
          this.breathIssue
            ? ((breathValue: BreathStringEnum) => {
                if (breathValue == "4") {
                  return "red";
                }
                if (breathValue == "3") {
                  return "orange";
                }
                return "green";
              })(this.breath)
            : "green"
        }`,
        answers: this.breathIssue
          ? ((breathValue: BreathStringEnum) => {
              switch (breathValue) {
                case "4":
                  return ["Très sévère"];
                case "3":
                  return ["Sévère"];
                case "2":
                  return ["Modérée"];
                case "1":
                  return ["Légère"];
                case "0":
                default:
                  return ["Aucune"];
              }
            })(this.breath)
          : ["Non"]
      },
      {
        key: "weight",
        label: "Poids",
        color: `${
          this.weightLossIssue
            ? ((currentWeight: number, weightLoss: number) => {
                if (weightLoss / currentWeight > 0.1) {
                  return "red";
                }
                return "green";
              })(this.currentWeight, this.weightLoss)
            : "green"
        }`,
        answers: this.weightLossIssue
          ? ((currentWeight: number, weightLoss: number) => {
              if (weightLoss / currentWeight > 0.1) {
                return [
                  `Poids actuel : ${this.currentWeight}kg`,
                  `Perte de poids importante : ${Math.round(
                    (weightLoss / currentWeight) * 100
                  )}%`
                ];
              }
              return [`Poids actuel : ${this.currentWeight}kg`];
            })(this.currentWeight, this.weightLoss)
          : [`Poids actuel : ${this.currentWeight}kg`]
      },
      {
        key: "dermatological",
        label: "Troubles dermatologiques",
        color: `${this.dermatologicalIssue ? "red" : "green"}`,
        answers: this.dermatologicalIssue
          ? ((
              skinRashIssue: boolean,
              skinRashLocation: string,
              itchingIssue: boolean,
              itchingLocation: string,
              dermatologicalTreatmentEfficient: boolean
            ) => {
              const toReturn: string[] = [];
              if (skinRashIssue) {
                toReturn.push(`Eruptions cutanées : ${skinRashLocation}`);
              }
              if (itchingIssue) {
                toReturn.push(`Démangeaisons : ${itchingLocation}`);
              }
              toReturn.push(
                `Traitement efficace : ${
                  dermatologicalTreatmentEfficient ? "oui" : "non"
                }`
              );
              return toReturn;
            })(
              this.skinRashIssue,
              this.skinRashLocation,
              this.itchingIssue,
              this.itchingLocation,
              this.dermatologicalTreatmentEfficient
            )
          : ["Non"]
      },
      {
        key: "nausea-vomiting",
        label: "Nausées/vomissements",
        color: `${
          this.nauseaVomitingIssue
            ? ((nauseaVomitingValue: NauseaVomitingStringEnum) => {
                if (nauseaVomitingValue == "2") {
                  return "red";
                }
                if (nauseaVomitingValue == "1") {
                  return "orange";
                }
                return "green";
              })(this.nauseaVomiting)
            : "green"
        }`,
        answers: this.nauseaVomitingIssue
          ? ((nauseaVomitingValue: NauseaVomitingStringEnum) => {
              switch (nauseaVomitingValue) {
                case "2":
                  return ["Sévère"];
                case "1":
                  return ["Modéré"];
                case "0":
                default:
                  return ["Faible"];
              }
            })(this.nauseaVomiting)
          : ["Non"]
      },
      {
        key: "diarrhea",
        label: "Diarrhées",
        color: `${
          this.diarrheaIssue
            ? ((diarrheaValue: DiarrheaStringEnum) => {
                if (diarrheaValue == "2") {
                  return "red";
                }
                if (diarrheaValue == "1") {
                  return "orange";
                }
                return "green";
              })(this.diarrhea)
            : "green"
        }`,
        answers: this.diarrheaIssue
          ? ((diarrheaValue: DiarrheaStringEnum) => {
              switch (diarrheaValue) {
                case "2":
                  return ["Sévère"];
                case "1":
                  return ["Modéré"];
                case "0":
                default:
                  return ["Faible"];
              }
            })(this.diarrhea)
          : ["Non"]
      },
      {
        key: "constipation",
        label: "Constipation",
        color: `${
          this.constipationIssue
            ? ((constipationValue: ConstipationStringEnum) => {
                if (constipationValue == "1") {
                  return "red";
                }

                return "green";
              })(this.constipation)
            : "green"
        }`,
        answers: this.constipationIssue
          ? ((constipationValue: ConstipationStringEnum) => {
              switch (constipationValue) {
                case "1":
                  return ["Persistante"];
                case "0":
                default:
                  return ["Occasionnelle"];
              }
            })(this.constipation)
          : ["Non"]
      },
      {
        key: "pain",
        label: "Douleurs",
        color: `${
          this.painIssue
            ? ((painValue: number) => {
                if (painValue >= 7) {
                  return "red";
                }
                if (painValue >= 4) {
                  return "orange";
                }
                return "green";
              })(this.pain)
            : "green"
        }`,
        answers: this.painIssue
          ? ((painValue: number) => {
              if (painValue >= 7) {
                return [`Douleur intense : ${painValue}/10`];
              }
              if (painValue >= 4) {
                return [`Douleur modérée : ${painValue}/10`];
              }
              return [`Douleur faible : ${painValue}/10`];
            })(this.pain)
          : ["Non"]
      },
      {
        key: "muscularPain",
        label: "Douleurs musculaires",
        color: `${this.muscularPainIssue ? "red" : "green"}`,
        answers: this.muscularPainIssue
          ? ((
              muscularPainLevel: MuscularPainLevelStringEnum,
              muscularPainLocation: string,
              muscularPainTreatmentEfficient: boolean
            ) => {
              const toReturn: string[] = [];
              if (muscularPainLevel == "2") {
                toReturn.push(`Douleur avec faiblesse sévère`);
              }
              if (muscularPainLevel == "1") {
                toReturn.push(`Douleur modérée avec faiblesse`);
              }
              if (muscularPainLevel == "0") {
                toReturn.push(`Douleur légère`);
              }

              toReturn.push(muscularPainLocation);

              toReturn.push(
                `Traitement efficace : ${
                  muscularPainTreatmentEfficient ? "oui" : "non"
                }`
              );
              return toReturn;
            })(
              this.muscularPainLevel,
              this.muscularPainLocation,
              this.muscularPainTreatmentEfficient
            )
          : ["Non"]
      },
      {
        key: "articularPain",
        label: "Douleurs articulaires",
        color: `${this.articularPainIssue ? "red" : "green"}`,
        answers: this.articularPainIssue
          ? ((
              articularPainLevel: ArticularPainLevelStringEnum,
              articularPainLocation: string,
              articularPainTreatmentEfficient: boolean
            ) => {
              const toReturn: string[] = [];
              if (articularPainLevel == "2") {
                toReturn.push(`Douleur avec faiblesse sévère`);
              }
              if (articularPainLevel == "1") {
                toReturn.push(`Douleur modérée avec faiblesse`);
              }
              if (articularPainLevel == "0") {
                toReturn.push(`Douleur légère`);
              }

              toReturn.push(articularPainLocation);

              toReturn.push(
                `Traitement efficace : ${
                  articularPainTreatmentEfficient ? "oui" : "non"
                }`
              );
              return toReturn;
            })(
              this.articularPainLevel,
              this.articularPainLocation,
              this.articularPainTreatmentEfficient
            )
          : ["Non"]
      },
      {
        key: "otherPain",
        label: "Douleurs autres",
        color: `${this.otherPainIssue ? "red" : "green"}`,
        answers: this.otherPainIssue
          ? ((
              otherPainLocation: string,
              otherPainTreatmentEfficient: boolean
            ) => {
              const toReturn: string[] = [];

              toReturn.push(otherPainLocation);

              toReturn.push(
                `Traitement efficace : ${
                  otherPainTreatmentEfficient ? "oui" : "non"
                }`
              );
              return toReturn;
            })(this.otherPainLocations, this.otherPainTreatmentEfficient)
          : ["Non"]
      }
    ];
  }
}
